import React,{useEffect} from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import SEOHead from '../components/common/SEOHead';

const {FiServer,FiMail,FiGlobe,FiLayers,FiShield,FiClock,FiUsers,FiCheck,FiArrowRight}=FiIcons;

const Services=()=> {
  useEffect(()=> {
    console.log('Services page mounted');
    console.log('Current URL hash:',window.location.hash);
    // Handle hash-based navigation with multiple attempts 
    const handleHashNavigation=()=> {
      const hash=window.location.hash.replace('#/','').replace('#','');
      console.log('Processed hash:',hash);
      if (hash && hash !=='services') {
        const element=document.getElementById(hash);
        console.log('Looking for element with ID:',hash,'Found:',element);
        if (element) {
          console.log('Scrolling to element:',hash);
          element.scrollIntoView({behavior: 'smooth',block: 'start',inline: 'nearest'});
        } else {
          // Try again after a short delay in case the DOM isn't ready 
          setTimeout(()=> {
            const retryElement=document.getElementById(hash);
            console.log('Retry - Looking for element:',hash,'Found:',retryElement);
            if (retryElement) {
              retryElement.scrollIntoView({behavior: 'smooth',block: 'start',inline: 'nearest'});
            }
          },500);
        }
      }
    };
    // Try immediately 
    handleHashNavigation();
    // Also try after component is fully mounted 
    const timer=setTimeout(handleHashNavigation,100);
    return ()=> clearTimeout(timer);
  },[]);

  const hostingFeatures=[ 
    'Shared hosting solutions for smaller businesses','99.9% uptime guarantee','Enterprise-grade security features','24/7 technical support','Email hosting with custom domains','Migration services','Backup and disaster recovery' 
  ];

  const emailFeatures=[ 
    'Professional email accounts with custom domains','Secure email hosting with spam protection','Webmail access for anywhere connectivity','Email archiving and backup solutions','Advanced security with encryption','Enterprise-grade functionality','Seamless migration services' 
  ];

  const designFeatures=[ 
    'Custom website design and development','Mobile-responsive implementations','E-commerce functionality','SEO-friendly structure development','Website redesign and modernisation','Template-based solutions','Content management systems' 
  ];

  const domainFeatures=[ 
    'Domain name registration and checking','Domain renewal management','DNS management and configuration','Domain privacy protection','Portfolio management for multiple domains','Domain transfer services' 
  ];

  const scrollToPricingSection=(sectionId)=> {
    // Use HashRouter navigation instead of window.location.href 
    window.location.hash=`#/pricing#${sectionId}`;
  };

  const scrollToContactForm=()=> {
    // Use HashRouter navigation instead of window.location.href 
    window.location.hash=`#/contact#contact-form`;
  };

  console.log('Services component rendered');
  return ( 
    <motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} className="pt-20" > 
      <SEOHead 
        title="Web Hosting, Email & Design Services | Primehost South Africa"
        description="Comprehensive digital services including web hosting, email hosting, web design and domain registration. 99.9% uptime guarantee, 24/7 support, based in Midrand, South Africa."
        keywords="web hosting services, email hosting, web design, domain registration, South Africa hosting, Midrand hosting, website design, professional hosting"
      />

      {/* Hero Section */} 
      <section style={{paddingTop: '50px',paddingBottom: '50px'}} className="bg-gradient-to-br from-gray-50 to-white py-20"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <motion.div initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} transition={{duration: 0.6}} className="text-center" > 
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"> <span style={{color: '#FF5841'}}>Comprehensive</span>{' '} <span className="text-gray-900">digital solutions</span> </h1> 
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6"> From reliable web hosting to stunning website design,we provide integrated solutions that combine technical excellence with local expertise. </p> 
          </motion.div> 
        </div> 
      </section> 

      {/* Web Hosting Services */} 
      <section style={{paddingTop: '20px',paddingBottom: '20px'}} id="hosting" className="py-10 bg-white scroll-mt-20"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"> 
            <motion.div initial={{opacity: 0,x: -50}} whileInView={{opacity: 1,x: 0}} transition={{duration: 0.6}} viewport={{once: true}} > 
              <div className="bg-primary-100 p-4 rounded-2xl w-16 h-16 flex items-center justify-center mb-6"> 
                <SafeIcon icon={FiServer} className="w-8 h-8 text-primary-500" /> 
              </div> 
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6"> Web hosting services </h2> 
              <p className="text-lg text-gray-600 mb-8"> Reliable,high-performance hosting solutions designed for South African businesses. Our hosting infrastructure ensures your website stays online and performs optimally. </p> 
              <div className="space-y-3 mb-8"> 
                {hostingFeatures.map((feature,index)=> ( 
                  <div key={index} className="flex items-start space-x-3"> 
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mt-0.5" /> 
                    <span className="text-gray-700">{feature}</span> 
                  </div> 
                ))} 
              </div> 
              <div className="flex flex-col sm:flex-row gap-4"> 
                <button onClick={()=> scrollToPricingSection('hosting-plans')} className="bg-primary-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-600 transition-colors duration-200 text-center" > View hosting plans </button> 
                <button onClick={scrollToContactForm} className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:border-primary-500 hover:text-primary-500 transition-colors duration-200 text-center" > Get started </button> 
              </div> 
            </motion.div> 
            <motion.div initial={{opacity: 0,x: 50}} whileInView={{opacity: 1,x: 0}} transition={{duration: 0.6,delay: 0.2}} viewport={{once: true}} > 
              <img src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Modern server infrastructure and web hosting" className="rounded-2xl shadow-xl" /> 
            </motion.div> 
          </div> 
        </div> 
      </section> 

      {/* Email Hosting Services */} 
      <section id="email" className="py-20 bg-gray-50 scroll-mt-20"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"> 
            <motion.div initial={{opacity: 0,x: -50}} whileInView={{opacity: 1,x: 0}} transition={{duration: 0.6}} viewport={{once: true}} className="order-2 lg:order-1" > 
              <img src="https://images.unsplash.com/photo-1596526131083-e8c633c948d2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Professional email communication and business correspondence" className="rounded-2xl shadow-xl" /> 
            </motion.div> 
            <motion.div initial={{opacity: 0,x: 50}} whileInView={{opacity: 1,x: 0}} transition={{duration: 0.6,delay: 0.2}} viewport={{once: true}} className="order-1 lg:order-2" > 
              <div className="bg-teal-100 p-4 rounded-2xl w-16 h-16 flex items-center justify-center mb-6"> 
                <SafeIcon icon={FiMail} className="w-8 h-8 text-teal-700" /> 
              </div> 
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6"> Email hosting services </h2> 
              <p className="text-lg text-gray-600 mb-8"> Professional email solutions that enhance your business communication. Secure,reliable email hosting with your custom domain for a professional image. </p> 
              <div className="space-y-3 mb-8"> 
                {emailFeatures.map((feature,index)=> ( 
                  <div key={index} className="flex items-start space-x-3"> 
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mt-0.5" /> 
                    <span className="text-gray-700">{feature}</span> 
                  </div> 
                ))} 
              </div> 
              <div className="flex flex-col sm:flex-row gap-4"> 
                <button onClick={()=> scrollToPricingSection('hosting-plans')} className="bg-teal-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-800 transition-colors duration-200 text-center" > View email plans </button> 
                <button onClick={scrollToContactForm} className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:border-teal-700 hover:text-teal-700 transition-colors duration-200 text-center" > Get started </button> 
              </div> 
            </motion.div> 
          </div> 
        </div> 
      </section> 

      {/* Web Design Services */} 
      <section id="design" className="py-20 bg-white scroll-mt-20"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"> 
            <motion.div initial={{opacity: 0,x: -50}} whileInView={{opacity: 1,x: 0}} transition={{duration: 0.6}} viewport={{once: true}} > 
              <div className="bg-primary-100 p-4 rounded-2xl w-16 h-16 flex items-center justify-center mb-6"> 
                <SafeIcon icon={FiLayers} className="w-8 h-8 text-primary-500" /> 
              </div> 
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6"> Web design services </h2> 
              <p className="text-lg text-gray-600 mb-8"> Custom website design and development that captures your brand and engages your audience. From simple brochure sites to complex e-commerce platforms,we create digital experiences that convert. </p> 
              <div className="space-y-3 mb-8"> 
                {designFeatures.map((feature,index)=> ( 
                  <div key={index} className="flex items-start space-x-3"> 
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mt-0.5" /> 
                    <span className="text-gray-700">{feature}</span> 
                  </div> 
                ))} 
              </div> 
              <div className="flex flex-col sm:flex-row gap-4"> 
                <button onClick={()=> scrollToPricingSection('design-packages')} className="bg-primary-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-600 transition-colors duration-200 text-center" > View design packages </button> 
                <Link to="/portfolio" className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:border-primary-500 hover:text-primary-500 transition-colors duration-200 text-center" > View portfolio </Link> 
              </div> 
            </motion.div> 
            <motion.div initial={{opacity: 0,x: 50}} whileInView={{opacity: 1,x: 0}} transition={{duration: 0.6,delay: 0.2}} viewport={{once: true}} > 
              <img src="https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Creative web design and development process" className="rounded-2xl shadow-xl" /> 
            </motion.div> 
          </div> 
        </div> 
      </section> 

      {/* Domain Registration Services */} 
      <section id="domains" className="py-20 bg-gray-50 scroll-mt-20"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"> 
            <motion.div initial={{opacity: 0,x: -50}} whileInView={{opacity: 1,x: 0}} transition={{duration: 0.6}} viewport={{once: true}} className="order-2 lg:order-1" > 
              <img src="https://media.phst.co.za/file/ph/primehost-services-domains.jpg" alt="Domain registration and management services" className="rounded-2xl shadow-xl" /> 
            </motion.div> 
            <motion.div initial={{opacity: 0,x: 50}} whileInView={{opacity: 1,x: 0}} transition={{duration: 0.6,delay: 0.2}} viewport={{once: true}} className="order-1 lg:order-2" > 
              <div className="bg-teal-100 p-4 rounded-2xl w-16 h-16 flex items-center justify-center mb-6"> 
                <SafeIcon icon={FiGlobe} className="w-8 h-8 text-teal-700" /> 
              </div> 
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6"> Domain registration services </h2> 
              <p className="text-lg text-gray-600 mb-8"> Secure your perfect domain name and establish your online identity. We handle everything from registration to ongoing management,ensuring your digital assets are protected. </p> 
              <div className="space-y-3 mb-8"> 
                {domainFeatures.map((feature,index)=> ( 
                  <div key={index} className="flex items-start space-x-3"> 
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mt-0.5" /> 
                    <span className="text-gray-700">{feature}</span> 
                  </div> 
                ))} 
              </div> 
              <div className="flex flex-col sm:flex-row gap-4"> 
                <button onClick={()=> scrollToPricingSection('domains')} className="bg-teal-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-800 transition-colors duration-200 text-center" > View domain pricing </button> 
                <button onClick={scrollToContactForm} className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:border-teal-700 hover:text-teal-700 transition-colors duration-200 text-center" > Check availability </button> 
              </div> 
            </motion.div> 
          </div> 
        </div> 
      </section> 

      {/* Why Choose Us */} 
      <section className="py-20 bg-white"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <motion.div initial={{opacity: 0,y: 20}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.6}} viewport={{once: true}} className="text-center mb-16" > 
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6"> Why choose Primehost for your digital services? </h2> 
            <p className="text-xl text-gray-600 max-w-3xl mx-auto"> We combine technical excellence with local expertise to deliver comprehensive solutions that drive your business forward. </p> 
          </motion.div> 
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8"> 
            {[ 
              {icon: FiShield,title: 'Enterprise-grade security',description: 'Multi-layered security protocols protect your digital assets with advanced threat detection and prevention.'},{icon: FiUsers,title: 'Local South African expertise',description: 'Based in Waterfall,Midrand,we understand the unique needs of South African businesses.'},{icon: FiClock,title: '24/7 technical support',description: 'Round-the-clock assistance from experienced professionals who are committed to your success.'} 
            ].map((feature,index)=> ( 
              <motion.div key={index} initial={{opacity: 0,y: 20}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.6,delay: index * 0.1}} viewport={{once: true}} className="text-center" > 
                <div className="bg-primary-100 p-4 rounded-2xl w-16 h-16 flex items-center justify-center mx-auto mb-6"> 
                  <SafeIcon icon={feature.icon} className="w-8 h-8 text-primary-500" /> 
                </div> 
                <h3 className="text-xl font-semibold text-gray-900 mb-4"> {feature.title} </h3> 
                <p className="text-gray-600 leading-relaxed"> {feature.description} </p> 
              </motion.div> 
            ))} 
          </div> 
        </div> 
      </section> 

      {/* CTA Section */} 
      <section className="py-20" style={{backgroundColor: '#D14932'}}> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"> 
          <motion.div initial={{opacity: 0,y: 20}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.6}} viewport={{once: true}} > 
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6"> Ready to transform your digital presence? </h2> 
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto"> Let us help you build a robust digital foundation with our comprehensive hosting,email,design,and domain services. </p> 
            <div className="flex flex-col sm:flex-row gap-4 justify-center"> 
              <button onClick={scrollToContactForm} className="bg-white text-red-700 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200" > Get started today </button> 
              <Link to="/pricing" className="border border-red-300 text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-600 transition-colors duration-200" > View pricing </Link> 
            </div> 
          </motion.div> 
        </div> 
      </section> 
    </motion.div> 
  );
};

export default Services;